import { Component, OnDestroy, OnInit } from '@angular/core'
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms'
import { TranslateService } from '@ngx-translate/core'
import { Subscription } from 'rxjs'
import { LabelToId } from 'src/app/common/input-utils'
import { Datum } from 'src/app/models/common/datum'
import {
  ServicePlanFieldError,
  ServicePlanStepComponent,
} from 'src/app/models/service-plan/service-plan-field-error'
import { ServicePlanStateService } from 'src/app/services/service-plan/service-plan-state.service'

type FirstSoundsYear = 'y1' | 'y2' | 'y3'
type LslFrequency = 'weekly' | 'fortnightly' | 'every4Weeks' | 'none'

// Like Validators.required, but also rejects a whitespace-only string — a clinical
// justification of "   " should not count as "provided".
function requiredNonBlankValidator(
  control: AbstractControl,
): ValidationErrors | null {
  const value = control.value
  if (value === null || value === undefined) {
    return { required: true }
  }
  if (typeof value === 'string' && value.trim().length === 0) {
    return { required: true }
  }
  return null
}

// Step 6 — Hearing technology & language support (story e8-5), shown unconditionally for every
// plan type. A four-deep conditional-required chain (First Sounds Yes/No -> year -> conditional
// L&SL frequency -> clinical-framework-match -> conditional mismatch reason), built on
// PlanTypeStepComponent's dynamic-validator-cascade pattern repeated top-down for each field in
// the chain, with every now-inapplicable downstream value cleared (validators + null) on every
// upstream change, matching e8-2's own code-review precedent (AC6).
@Component({
  selector: 'app-service-plan-hearing-language-support-step',
  templateUrl: './hearing-language-support-step.component.html',
  styleUrls: ['./hearing-language-support-step.component.scss'],
  standalone: false,
})
export class HearingLanguageSupportStepComponent
  implements OnInit, OnDestroy, ServicePlanStepComponent
{
  form!: FormGroup<{
    onFirstSounds: FormControl<boolean | null>
    firstSoundsYear: FormControl<FirstSoundsYear | null>
    lslFrequency: FormControl<LslFrequency | null>
    clinicalFrameworkMatch: FormControl<boolean | null>
    clinicalFrameworkMismatchReason: FormControl<string | null>
  }>

  onFirstSoundsLabel = ''
  onFirstSoundsControlId = ''
  firstSoundsYearLabel = ''
  firstSoundsYearControlId = ''
  firstSoundsYearOptions: Datum[] = []
  lslFrequencyLabel = ''
  lslFrequencyControlId = ''
  lslFrequencyOptions: Datum[] = []
  clinicalFrameworkMatchLabel = ''
  clinicalFrameworkMatchControlId = ''
  clinicalFrameworkMismatchReasonLabel = ''
  clinicalFrameworkMismatchReasonControlId = ''

  private subscription?: Subscription

  constructor(
    private state: ServicePlanStateService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.onFirstSoundsLabel = this.translate.instant(
      'SERVICE_PLAN.STEP.HEARING_LANGUAGE_SUPPORT.ON_FIRST_SOUNDS_LABEL',
    )
    this.onFirstSoundsControlId = LabelToId(this.onFirstSoundsLabel)

    this.firstSoundsYearLabel = this.translate.instant(
      'SERVICE_PLAN.STEP.HEARING_LANGUAGE_SUPPORT.FIRST_SOUNDS_YEAR_LABEL',
    )
    this.firstSoundsYearControlId = LabelToId(this.firstSoundsYearLabel)
    // Session-count/fee subtext sourced verbatim from the raw UI mockup's FEES.firstSounds
    // schedule (also ServicePlan-GuidedFormDesign.md Section 4) — per Lee's direct feedback,
    // this additional detail shown in the mockup but not carried into the design doc/epics AC
    // should still be surfaced to the clinician, reversing this story's original "Resolved"
    // omission. Hardcoded display strings, not CurrencyPipe, so the figures are deterministic
    // regardless of the app's configured locale (same reasoning as
    // BaselinePackageStepComponent's feeAmountDisplay).
    this.firstSoundsYearOptions = [
      {
        label: this.translate.instant(
          'SERVICE_PLAN.STEP.HEARING_LANGUAGE_SUPPORT.FIRST_SOUNDS_YEAR_OPTION_Y1',
        ),
        value: 'y1',
        description: this.translate.instant(
          'SERVICE_PLAN.STEP.HEARING_LANGUAGE_SUPPORT.FIRST_SOUNDS_YEAR_OPTION_Y1_SUB',
        ),
      },
      {
        label: this.translate.instant(
          'SERVICE_PLAN.STEP.HEARING_LANGUAGE_SUPPORT.FIRST_SOUNDS_YEAR_OPTION_Y2',
        ),
        value: 'y2',
        description: this.translate.instant(
          'SERVICE_PLAN.STEP.HEARING_LANGUAGE_SUPPORT.FIRST_SOUNDS_YEAR_OPTION_Y2_SUB',
        ),
      },
      {
        label: this.translate.instant(
          'SERVICE_PLAN.STEP.HEARING_LANGUAGE_SUPPORT.FIRST_SOUNDS_YEAR_OPTION_Y3',
        ),
        value: 'y3',
        description: this.translate.instant(
          'SERVICE_PLAN.STEP.HEARING_LANGUAGE_SUPPORT.FIRST_SOUNDS_YEAR_OPTION_Y3_SUB',
        ),
      },
    ]

    this.lslFrequencyLabel = this.translate.instant(
      'SERVICE_PLAN.STEP.HEARING_LANGUAGE_SUPPORT.LSL_FREQUENCY_LABEL',
    )
    this.lslFrequencyControlId = LabelToId(this.lslFrequencyLabel)
    this.lslFrequencyOptions = [
      {
        label: this.translate.instant(
          'SERVICE_PLAN.STEP.HEARING_LANGUAGE_SUPPORT.LSL_FREQUENCY_OPTION_WEEKLY',
        ),
        value: 'weekly',
        description: this.translate.instant(
          'SERVICE_PLAN.STEP.HEARING_LANGUAGE_SUPPORT.LSL_FREQUENCY_OPTION_WEEKLY_SUB',
        ),
      },
      {
        label: this.translate.instant(
          'SERVICE_PLAN.STEP.HEARING_LANGUAGE_SUPPORT.LSL_FREQUENCY_OPTION_FORTNIGHTLY',
        ),
        value: 'fortnightly',
        description: this.translate.instant(
          'SERVICE_PLAN.STEP.HEARING_LANGUAGE_SUPPORT.LSL_FREQUENCY_OPTION_FORTNIGHTLY_SUB',
        ),
      },
      {
        label: this.translate.instant(
          'SERVICE_PLAN.STEP.HEARING_LANGUAGE_SUPPORT.LSL_FREQUENCY_OPTION_EVERY_4_WEEKS',
        ),
        value: 'every4Weeks',
        description: this.translate.instant(
          'SERVICE_PLAN.STEP.HEARING_LANGUAGE_SUPPORT.LSL_FREQUENCY_OPTION_EVERY_4_WEEKS_SUB',
        ),
      },
      {
        label: this.translate.instant(
          'SERVICE_PLAN.STEP.HEARING_LANGUAGE_SUPPORT.LSL_FREQUENCY_OPTION_NONE',
        ),
        value: 'none',
        description: this.translate.instant(
          'SERVICE_PLAN.STEP.HEARING_LANGUAGE_SUPPORT.LSL_FREQUENCY_OPTION_NONE_SUB',
        ),
      },
    ]

    this.clinicalFrameworkMatchLabel = this.translate.instant(
      'SERVICE_PLAN.STEP.HEARING_LANGUAGE_SUPPORT.CLINICAL_FRAMEWORK_MATCH_LABEL',
    )
    this.clinicalFrameworkMatchControlId = LabelToId(
      this.clinicalFrameworkMatchLabel,
    )

    this.clinicalFrameworkMismatchReasonLabel = this.translate.instant(
      'SERVICE_PLAN.STEP.HEARING_LANGUAGE_SUPPORT.CLINICAL_FRAMEWORK_MISMATCH_REASON_LABEL',
    )
    this.clinicalFrameworkMismatchReasonControlId = LabelToId(
      this.clinicalFrameworkMismatchReasonLabel,
    )

    const payload = this.state.getPayload().hearingLanguageSupport
    this.form = new FormGroup({
      onFirstSounds: new FormControl<boolean | null>(payload.onFirstSounds, {
        validators: Validators.required,
      }),
      firstSoundsYear: new FormControl<FirstSoundsYear | null>(
        payload.firstSoundsYear,
      ),
      lslFrequency: new FormControl<LslFrequency | null>(payload.lslFrequency),
      clinicalFrameworkMatch: new FormControl<boolean | null>(
        payload.clinicalFrameworkMatch,
      ),
      clinicalFrameworkMismatchReason: new FormControl<string | null>(
        payload.clinicalFrameworkMismatchReason,
      ),
    })

    this.applyValidatorCascade()
    this.writePayload()

    this.subscription = this.form.valueChanges.subscribe(() => {
      this.applyValidatorCascade()
      this.writePayload()
    })
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe()
  }

  get showFirstSoundsYear(): boolean {
    return this.form?.controls.onFirstSounds.value === true
  }

  get showLslFrequency(): boolean {
    const onFirstSounds = this.form?.controls.onFirstSounds.value
    const firstSoundsYear = this.form?.controls.firstSoundsYear.value
    return (
      onFirstSounds === false ||
      (onFirstSounds === true && firstSoundsYear === 'y3')
    )
  }

  get showClinicalFrameworkMatch(): boolean {
    if (!this.form) {
      return false
    }
    return this.isHearingSelectionComplete(
      this.form.controls.onFirstSounds.value,
      this.form.controls.firstSoundsYear.value,
      this.form.controls.lslFrequency.value,
    )
  }

  get showMismatchReason(): boolean {
    return this.form?.controls.clinicalFrameworkMatch.value === false
  }

  // Bound to app-textarea's `required` input — reflects whether the control currently
  // carries Validators.required (set/cleared by applyValidatorCascade), not a static value.
  get isMismatchReasonRequired(): boolean {
    return (
      this.form?.controls.clinicalFrameworkMismatchReason.hasValidator(
        requiredNonBlankValidator,
      ) ?? false
    )
  }

  // ServicePlanStepComponent contract — the shell's Continue button reads this directly
  // (non-mutating), unlike isValid() below which calls markAllAsTouched() on click.
  get isLiveValid(): boolean {
    return this.form?.valid ?? false
  }

  isValid(): boolean {
    this.form.markAllAsTouched()
    return this.form.valid
  }

  getValidationErrors(): ServicePlanFieldError[] {
    const errors: ServicePlanFieldError[] = []
    const controls = this.form.controls

    if (controls.onFirstSounds.invalid) {
      errors.push({
        controlId: this.onFirstSoundsControlId,
        messageKey: 'SERVICE_PLAN.VALIDATION.ON_FIRST_SOUNDS_REQUIRED',
      })
    }
    if (controls.firstSoundsYear.invalid) {
      errors.push({
        controlId: this.firstSoundsYearControlId,
        messageKey: 'SERVICE_PLAN.VALIDATION.FIRST_SOUNDS_YEAR_REQUIRED',
      })
    }
    if (controls.lslFrequency.invalid) {
      errors.push({
        controlId: this.lslFrequencyControlId,
        messageKey: 'SERVICE_PLAN.VALIDATION.LSL_FREQUENCY_REQUIRED',
      })
    }
    if (controls.clinicalFrameworkMatch.invalid) {
      errors.push({
        controlId: this.clinicalFrameworkMatchControlId,
        messageKey: 'SERVICE_PLAN.VALIDATION.CLINICAL_FRAMEWORK_MATCH_REQUIRED',
      })
    }
    if (controls.clinicalFrameworkMismatchReason.invalid) {
      errors.push({
        controlId: this.clinicalFrameworkMismatchReasonControlId,
        messageKey:
          'SERVICE_PLAN.VALIDATION.CLINICAL_FRAMEWORK_MISMATCH_REASON_REQUIRED',
      })
    }
    return errors
  }

  // Rule 8's exact completeness logic: onFirstSounds === null -> incomplete; === true ->
  // requires firstSoundsYear set, and additionally lslFrequency set when year is 'y3';
  // === false -> requires lslFrequency set directly.
  private isHearingSelectionComplete(
    onFirstSounds: boolean | null,
    firstSoundsYear: FirstSoundsYear | null,
    lslFrequency: LslFrequency | null,
  ): boolean {
    if (onFirstSounds === null) {
      return false
    }
    if (onFirstSounds === true) {
      if (!firstSoundsYear) {
        return false
      }
      return firstSoundsYear === 'y3' ? !!lslFrequency : true
    }
    return !!lslFrequency
  }

  // Validator cascade, applied top-down (mirrors PlanTypeStepComponent's
  // applyHasNdisValidator), called once from ngOnInit and again on every valueChanges
  // tick. Each now-inapplicable control has its validators cleared and its value nulled
  // (emitEvent: false) so a stale answer is never carried forward into the payload (AC6).
  private applyValidatorCascade(): void {
    const controls = this.form.controls

    const onFirstSounds = controls.onFirstSounds.value
    this.applyRequiredIf(controls.firstSoundsYear, onFirstSounds === true)

    const firstSoundsYear = controls.firstSoundsYear.value
    const lslRequired =
      onFirstSounds === false ||
      (onFirstSounds === true && firstSoundsYear === 'y3')
    this.applyRequiredIf(controls.lslFrequency, lslRequired)

    const lslFrequency = controls.lslFrequency.value
    const selectionComplete = this.isHearingSelectionComplete(
      onFirstSounds,
      firstSoundsYear,
      lslFrequency,
    )
    this.applyRequiredIf(controls.clinicalFrameworkMatch, selectionComplete)

    const clinicalFrameworkMatch = controls.clinicalFrameworkMatch.value
    this.applyRequiredIf(
      controls.clinicalFrameworkMismatchReason,
      clinicalFrameworkMatch === false,
      requiredNonBlankValidator,
    )
  }

  private applyRequiredIf<T>(
    control: FormControl<T | null>,
    required: boolean,
    validator: ValidatorFn = Validators.required,
  ): void {
    if (required) {
      control.setValidators(validator)
    } else {
      control.clearValidators()
      if (control.value !== null) {
        control.setValue(null, { emitEvent: false })
      }
    }
    control.updateValueAndValidity({ emitEvent: false })
  }

  private writePayload(): void {
    const value = this.form.getRawValue()
    this.state.updatePayload({
      hearingLanguageSupport: {
        ...this.state.getPayload().hearingLanguageSupport,
        onFirstSounds: value.onFirstSounds,
        firstSoundsYear: value.firstSoundsYear,
        lslFrequency: value.lslFrequency,
        clinicalFrameworkMatch: value.clinicalFrameworkMatch,
        clinicalFrameworkMismatchReason: value.clinicalFrameworkMismatchReason,
      },
    })
  }
}
